import { Box, Card, Flex, Grid, Text, Tag, TagLabel, Button, Icon } from '@chakra-ui/react'
import React, { useState, useMemo } from 'react'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import FilterButton from './base/FilterButton'
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { statusColors } from '../../themes/components/tag'
import { useRole } from '../../hooks/auth/useRole'
import { fetchEmployeeAssets, deleteCompanyAsset } from '../../api/assets/assets'
import formatDate from '../../helpers/formatDate'
import AddAssetsModal from '../modals/addAssetsModal'
import { PlusIcon, Trash04Icon } from '../../assets/icons'
import { fetchEmployeeList } from '../../api/workforce/workforce'
import { useTranslation } from 'react-i18next'
import AvatarCell from './base/AvatarCell'

interface AssetsTableProps {
  employeeId?: string
  isAdminView?: boolean
  employee?: { id: string; first_name: string; last_name: string; profile_picture?: string }
}
const AssetsTable: React.FC<AssetsTableProps> = ({ employeeId, isAdminView = false, employee }) => {
  const { t } = useTranslation()

  const [filters, setFilters] = useState<{ [key: string]: string[] }>({})
  const [searchTerm, setSearchTerm] = useState('')
  // Removed unused duration and videoRef

  const [page, setPage] = useState(1)
  const itemsPerPage = 15
  const [selectedRows, setSelectedRows] = useState<number[]>([])

  // Modal state for editing and adding
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<RowData | null>(null)

  const assets = useQuery({
    queryKey: ['assets', page, searchTerm, filters, employeeId],
    queryFn: () =>
      fetchEmployeeAssets({
        page,
        pageSize: itemsPerPage,
        search: searchTerm,
        filters,
        status: '',
        ...(employeeId && { assigned_to: employeeId }),
      }),
    // keep prefetched pages fresh for short time to avoid immediate refetch
    staleTime: 30000,
  })

  const queryClient = useQueryClient()
  const deleteAssetMutation = useMutation({
    mutationFn: deleteCompanyAsset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] })
    },
  })

  const handleDeleteAssets = () => {
    selectedRows.forEach((assetId) => {
      deleteAssetMutation.mutate(assetId)
    })
    setSelectedRows([])
  }

  const { role } = useRole()
  const columns: Column[] = [
    ...(!employeeId && role !== 'user'
      ? [{ label: '', accessor: 'select', type: 'select', width: '5%' as const }]
      : []),
    ...(employeeId && isAdminView ? [{ label: '', accessor: 'select', type: 'select', width: '5%' as const }] : []),
    {
      label: t('workforce.assets.givenTo'),
      accessor: 'assigned_to',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.assigned_to?.profile_picture} name={`${row.assigned_to?.first_name || ''} ${row.assigned_to?.last_name || ''}`} />
      ),
    },
    {
      label: t('workforce.assets.deviceType'),
      accessor: 'type',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => {
        const typeLabels: Record<string, string> = {
          computer: t('workforce.assets.types.computer'),
          office: t('workforce.assets.types.office'),
          phone: t('workforce.assets.types.phone'),
          key: t('workforce.assets.types.key'),
          vehicle: t('workforce.assets.types.vehicle'),
          other: t('workforce.assets.types.other'),
        }
        return <Text textStyle="b-xs">{typeLabels[row.type] || row.type}</Text>
      },
    },
    {
      label: t('workforce.assets.dateGiven'),
      accessor: 'date_handed_in',
      type: 'custom',
      Cell: ({ row }) => <Text textStyle="l-xs">{formatDate(row.date_handed_out)}</Text>,
    },
    {
      label: t('common.fields.status'),
      accessor: 'status',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => {
        if (row?.status) {
          let colorInfo
          if (row.status === 'handed_out') {
            colorInfo = statusColors.success
          } else if (row.status === 'maintenance') {
            colorInfo = statusColors.warning
          } else if (row.status === 'handed_in') {
            colorInfo = statusColors.error
          }
          if (!colorInfo) return null
          return (
            <Tag
              borderRadius="14px"
              h="28px"
              bg={colorInfo.bg}
              color={colorInfo.color}
              borderWidth="1px"
              borderColor={colorInfo.border}
            >
              <TagLabel textStyle="h-xxs" pb={'1px'}>
                {row.status === 'handed_out'
                  ? t('workforce.assets.statuses.handedOut')
                  : row.status === 'handed_in'
                    ? t('workforce.assets.statuses.handedIn')
                    : row.status === 'maintenance'
                      ? t('workforce.assets.statuses.maintenance')
                      : row.status}
              </TagLabel>
            </Tag>
          )
        }
      },
    },
  ]

  // --- Filter infrastructure (replaces TableSearch) ---

  const updateFilter = (key: string, values: string[]) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  // Infinite scroll recipient (assignee) filter -- only when no specific employeeId
  const [recipientFilterSearch, setRecipientFilterSearch] = useState('')
  const recipientFilterQuery = useInfiniteQuery({
    queryKey: ['filter-asset-recipients', 10, recipientFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchEmployeeList({ search: recipientFilterSearch, page: pageParam, pageSize: 10, filters: {}, minimal: true }),
    staleTime: 30000,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
    enabled: !employeeId,
  })
  const recipientFilterList = useMemo(
    () =>
      (recipientFilterQuery.data?.pages ?? []).flatMap((p: { data?: { id: string; first_name: string; last_name: string }[] }) =>
        Array.isArray(p?.data) ? p.data.map((e) => ({ id: e.id, name: `${e.first_name} ${e.last_name}` })) : [],
      ),
    [recipientFilterQuery.data?.pages],
  )

  // Static filter options
  const ASSET_TYPE_OPTIONS = useMemo(
    () => [
      { id: 'computer', name: t('workforce.assets.types.computer') },
      { id: 'office', name: t('workforce.assets.types.office') },
      { id: 'phone', name: t('workforce.assets.types.phone') },
      { id: 'key', name: t('workforce.assets.types.key') },
      { id: 'vehicle', name: t('workforce.assets.types.vehicle') },
      { id: 'other', name: t('workforce.assets.types.other') },
    ],
    [t],
  )

  const ASSET_STATUS_OPTIONS = useMemo(
    () => [
      { id: 'handed_out', name: t('workforce.assets.statuses.handedOut') },
      { id: 'handed_in', name: t('workforce.assets.statuses.handedIn') },
      { id: 'maintenance', name: t('workforce.assets.statuses.underMaintenance') },
    ],
    [t],
  )

  return (
    <Card width="100%" p="0">
      <Grid>
        <Box>
          <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} px="20px">
            <SearchInput
              onSearch={(term) => {
                setSearchTerm(term)
                setPage(1)
              }}
            />
            <Flex gap="8px">
              {selectedRows.length > 0 ? (
                <Button
                  onClick={handleDeleteAssets}
                  variant="error"
                  size="sm"
                  h="32px"
                  px="14px"
                  fontSize="13px"
                  fontWeight="600"
                  borderRadius="8px"
                >
                  <Icon fill="none" color="error.100" mr={2}>
                    <Trash04Icon />
                  </Icon>
                  {t('common.actions.delete')} ({selectedRows.length})
                </Button>
              ) : (
                <>
                  {!employeeId && (
                    <FilterButton
                      label={t('common.fields.assignee')}
                      options={recipientFilterList}
                      onApply={(selected) => updateFilter('assigned_to', selected)}
                      search={recipientFilterSearch}
                      setSearch={setRecipientFilterSearch}
                      onLoadMore={() => recipientFilterQuery.fetchNextPage()}
                      hasMore={!!recipientFilterQuery.hasNextPage}
                      isLoadingMore={recipientFilterQuery.isFetchingNextPage}
                      isLoading={recipientFilterQuery.isFetching && !recipientFilterQuery.isFetchingNextPage}
                    />
                  )}
                  <FilterButton
                    label={t('common.fields.type')}
                    options={ASSET_TYPE_OPTIONS}
                    onApply={(selected) => updateFilter('type', selected)}
                  />
                  <FilterButton
                    label={t('common.fields.status')}
                    options={ASSET_STATUS_OPTIONS}
                    onApply={(selected) => updateFilter('status', selected)}
                  />
                  {(role !== 'user' || isAdminView) && (
                    <Button
                      variant="mono"
                      size="sm"
                      h="32px"
                      px="14px"
                      fontSize="13px"
                      fontWeight="600"
                      borderRadius="8px"
                      bg="black"
                      color="white"
                      onClick={() => setAddModalOpen(true)}
                      gap="8px"
                    >
                      <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="white">
                        <PlusIcon />
                      </Icon>
                      {t('workforce.assets.addAsset')}
                    </Button>
                  )}
                </>
              )}
            </Flex>
          </Flex>
          {assets.isLoading ? (
            <LoadingTable columns={columns} />
          ) : (
            <Box minHeight="58vh">
              <MasterTable
                columns={columns}
                data={assets?.data?.data || []}
                onRowClick={(row) => {
                  setSelectedAsset(row)
                  setEditModalOpen(true)
                }}
                onOpenNewTab={() => {}}
                selectedRows={selectedRows}
                onSelectAll={(isChecked) => {
                  if (isChecked) {
                    setSelectedRows((assets?.data?.data || []).map((row: RowData) => row.id))
                  } else {
                    setSelectedRows([])
                  }
                }}
                onSelectRow={(id, isChecked) => {
                  if (isChecked) {
                    setSelectedRows((prev) => [...prev, id])
                  } else {
                    setSelectedRows((prev) => prev.filter((rowId) => rowId !== id))
                  }
                }}
                allSelected={
                  (assets?.data?.data || []).length > 0 && selectedRows.length === (assets?.data?.data || []).length
                }
                isIndeterminate={
                  (assets?.data?.data || []).length > 0 &&
                  selectedRows.length > 0 &&
                  selectedRows.length < (assets?.data?.data || []).length
                }
                tableType="assets"
              />

              {/* Modal for editing asset */}
              <AddAssetsModal
                isOpen={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                asset={selectedAsset}
                disableAssignee={!!employeeId}
              />

              {/* Modal for adding new asset: if opened from a user profile, preselect that user; otherwise allow selecting assignee */}
              <AddAssetsModal
                isOpen={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                {...(employeeId ? { preselectedEmployeeId: employeeId, preselectedEmployee: employee } : {})}
              />
            </Box>
          )}
          <Pagination
            currentPage={page}
            itemsPerPage={itemsPerPage}
            maxPage={assets?.data?.max_page || 0}
            totalItems={assets?.data?.count || 0}
            handlePreviousPage={() => setPage(page - 1)}
            handleNextPage={() => setPage(page + 1)}
            handlePageClick={(page: number) => setPage(page)}
          />
        </Box>
      </Grid>
    </Card>
  )
}

export default AssetsTable
